import { FC, RefObject, useEffect, useLayoutEffect } from "react";

import { useInView } from "../../hooks/useInView";
import { prefersReducedMotion } from "../../utils/prefersReducedMotion";

const DURATION = 1400;

// Decelerating: the number arrives at its value rather than stopping dead.
const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3);

// nl-BE throughout, matching the charts and utils/formatNumber — the client asked for
// 1.234,56 rather than 1,234.56.
const format = (value: number, decimals: number) =>
	new Intl.NumberFormat("nl-BE", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);

const decimalsOf = (element: HTMLElement) => Number(element.dataset.countDecimals ?? 0) || 0;

interface CountUpTextProps {
	/** Inline HTML. Any `<span data-count-to="80">` inside counts up when scrolled into view. */
	html: string;
	className?: string;
	/** `span` where the counter sits inside a sentence: a `<p>` nested in a `<p>` is invalid. */
	as?: "p" | "span";
}

/**
 * Renders an HTML string and animates the numbers marked inside it. Marking them in the
 * content — rather than parsing digits out of the copy — keeps prefixes, units and locale
 * formatting under the author's control, and leaves unmarked figures alone.
 */
const CountUpText: FC<CountUpTextProps> = ({ html, className, as = "p" }) => {
	const reduceMotion = prefersReducedMotion();
	const [ref, inView] = useInView<HTMLElement>({ disabled: reduceMotion });

	// Zero the counters before the browser paints. Without this, a counter already on screen
	// at load shows its final value and then visibly snaps back to zero to start.
	useLayoutEffect(() => {
		if (reduceMotion || !ref.current) return;
		ref.current.querySelectorAll<HTMLElement>("[data-count-to]").forEach((element) => {
			element.textContent = format(0, decimalsOf(element));
		});
	}, [html, reduceMotion, ref]);

	useEffect(() => {
		if (!inView || reduceMotion || !ref.current) return;

		const targets = Array.from(ref.current.querySelectorAll<HTMLElement>("[data-count-to]"))
			.map((element) => ({ element, to: Number(element.dataset.countTo), decimals: decimalsOf(element) }))
			.filter((target) => Number.isFinite(target.to));

		if (targets.length === 0) return;

		let frame = 0;
		const start = performance.now();

		const step = (now: number) => {
			const progress = Math.min((now - start) / DURATION, 1);
			const eased = easeOutCubic(progress);
			targets.forEach(({ element, to, decimals }) => {
				element.textContent = format(to * eased, decimals);
			});
			if (progress < 1) frame = requestAnimationFrame(step);
		};

		frame = requestAnimationFrame(step);
		return () => cancelAnimationFrame(frame);
	}, [inView, reduceMotion, html, ref]);

	// Written out rather than rendered through a variable tag, so each branch keeps its own
	// element ref type instead of collapsing to a union React cannot narrow.
	const props = { className, dangerouslySetInnerHTML: { __html: html } };

	return as === "span" ? (
		<span ref={ref as RefObject<HTMLSpanElement>} {...props} />
	) : (
		<p ref={ref as RefObject<HTMLParagraphElement>} {...props} />
	);
};

export { CountUpText };
